import React, { useEffect, useState } from "react";
import { paymentLinkRepo } from "../../services";
import { onlinePaymentService } from "../../services/onlinePayment";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { DEFAULT_SETTINGS, EXPIRE_DAYS_MAX, EXPIRE_DAYS_MIN, THANK_YOU_MAX, effectiveLinkStatus, isPendingLink, payUrl, payoutSummary } from "../../config/online-payment";
import { Badge, Button, Card, DataTable, Input, PageHead, StatCard, StatusBadge, Switch, Textarea, baht, formatDate, useAsync } from "../../components/ui";

export default function OnlinePayment() {
  const { orgId, organization, toast } = useApp();
  const { tx } = useI18n();
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState("");

  const { data: settings } = useAsync(() => onlinePaymentService.getSettings(orgId), [orgId], null);
  const { data: links, loading, reload } = useAsync(
    async () => (await paymentLinkRepo.all(orgId)).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))),
    [orgId],
    []
  );

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await onlinePaymentService.saveSettings(orgId, form);
      setForm(saved);
      toast(tx("บันทึกการตั้งค่าแล้ว", "Settings saved"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const check = async (row) => {
    setChecking(row.id);
    try {
      const result = await onlinePaymentService.checkPending({ orgId, link: row, organization });
      const messages = {
        paid: tx("ลูกค้าชำระแล้ว ปิดบิลและออกใบเสร็จเรียบร้อย", "Paid — receipt issued"),
        pending: tx("ยังไม่พบการชำระเงิน", "Still waiting for payment"),
        failed: tx("รายการชำระหมดอายุ/ไม่สำเร็จ", "The payment expired or failed"),
        unknown: tx("ตรวจสอบสถานะไม่สำเร็จ ลองใหม่อีกครั้ง", "Could not check status"),
      };
      toast(messages[result.status] || messages.unknown, result.status === "paid" ? "success" : "");
      reload();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setChecking("");
    }
  };

  const copy = async (row) => {
    try {
      await navigator.clipboard.writeText(payUrl(row.code));
      toast(tx("คัดลอกลิงก์แล้ว", "Link copied"), "success");
    } catch (e) {
      toast(tx("คัดลอกไม่สำเร็จ", "Copy failed"), "error");
    }
  };

  const cancel = async (row) => {
    if (!window.confirm(tx("ยกเลิกลิงก์นี้? ลูกค้าจะชำระผ่านลิงก์นี้ไม่ได้อีก", "Cancel this link?"))) return;
    await onlinePaymentService.cancelLink(orgId, row.id);
    reload();
  };

  const summary = payoutSummary(links);

  return (
    <>
      <PageHead
        title={tx("รับชำระเงินออนไลน์", "Online payment")}
        subtitle={tx("ส่งลิงก์ชำระให้ลูกค้าจ่ายด้วย QR ได้เอง และดูยอดที่รอโอนเข้าร้าน", "Send payment links; track payouts")}
        actions={<Button variant="primary" onClick={save} disabled={saving}>{tx("บันทึก", "Save")}</Button>}
      />

      <div className="grid-3 u-mb-16">
        <StatCard label={tx("ยอดรอโอนให้ร้าน", "Awaiting payout")} value={baht(summary.waitingAmount)} hint={tx(`${summary.waitingCount} รายการ`, `${summary.waitingCount} payments`)} />
        <StatCard label={tx("โอนให้ร้านแล้ว", "Paid out")} value={baht(summary.transferredAmount)} hint={tx(`${summary.transferredCount} รายการ`, `${summary.transferredCount} payments`)} />
        <StatCard label={tx("รับชำระออนไลน์ทั้งหมด", "Total received")} value={baht(summary.totalAmount)} />
      </div>
      <div className="field-hint u-mb-16">
        {tx("เงินที่ลูกค้าจ่ายผ่านลิงก์จะเข้าแพลตฟอร์มก่อน แล้วทีมงานโอนให้ร้านตามรอบ (ทีมงานเป็นผู้อัปเดตสถานะ 'โอนแล้ว')", "Customer payments reach the platform first; the team transfers them to the shop periodically.")}
      </div>

      <Card title={tx("ตั้งค่า", "Settings")}>
        <Switch label={tx("เปิดรับชำระผ่านลิงก์", "Accept payments via links")} checked={!!form.enabled} onChange={(checked) => setForm({ ...form, enabled: checked })} />
        {!form.enabled ? <div className="field-hint">{tx("ปิดอยู่: ลูกค้าเปิดลิงก์แล้วจะเห็นว่าร้านยังไม่เปิดรับชำระออนไลน์ (รับชำระ QR ที่เคาน์เตอร์ยังใช้ได้ตามปกติ)", "Off: customers see that online payment is unavailable.")}</div> : null}
        <div className="u-mt-16">
          <Input
            label={tx(`อายุลิงก์ (วัน ${EXPIRE_DAYS_MIN}-${EXPIRE_DAYS_MAX})`, "Link lifetime (days)")}
            type="number"
            min={EXPIRE_DAYS_MIN}
            max={EXPIRE_DAYS_MAX}
            value={form.linkExpireDays}
            onChange={(e) => setForm({ ...form, linkExpireDays: e.target.value })}
          />
          <Textarea
            label={tx("ข้อความขอบคุณหลังชำระ", "Thank-you message")}
            rows={3}
            maxLength={THANK_YOU_MAX}
            value={form.thankYou}
            onChange={(e) => setForm({ ...form, thankYou: e.target.value })}
          />
        </div>
      </Card>

      <h3 className="u-mt-24 u-mb-8" style={{ fontSize: 16, fontWeight: 800 }}>{tx("ลิงก์ชำระเงินทั้งหมด", "Payment links")}</h3>
      <DataTable
        loading={loading}
        rows={links || []}
        emptyText={tx("ยังไม่มีลิงก์ — สร้างจากปุ่ม 'ส่งลิงก์ชำระ/QR' ในใบแจ้งหนี้", "No links yet")}
        columns={[
          { key: "docNo", label: tx("เอกสาร", "Document"), render: (row) => row.docNo || row.title },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "amount", label: tx("ยอด", "Amount"), align: "right", render: (row) => baht(row.amount) },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) => (
              <>
                <StatusBadge status={effectiveLinkStatus(row)} />{" "}
                {isPendingLink(row) ? <Badge tone="amber">{tx("รอตรวจสอบ", "pending")}</Badge> : null}
                {row.status === "paid" ? <Badge tone={row.payoutStatus === "transferred" ? "green" : "gray"}>{row.payoutStatus === "transferred" ? tx("โอนแล้ว", "paid out") : tx("รอโอน", "awaiting payout")}</Badge> : null}
              </>
            ),
          },
          { key: "expireAt", label: tx("หมดอายุ", "Expires"), render: (row) => formatDate(row.expireAt) },
          {
            key: "action",
            label: "",
            render: (row) => (
              <div className="u-flex u-gap-4">
                {isPendingLink(row) ? <Button size="sm" variant="primary" disabled={checking === row.id} onClick={() => check(row)}>{checking === row.id ? tx("กำลังตรวจ...", "Checking...") : tx("ตรวจสอบสถานะ", "Check status")}</Button> : null}
                {effectiveLinkStatus(row) === "active" ? <Button size="sm" onClick={() => copy(row)}>{tx("คัดลอกลิงก์", "Copy link")}</Button> : null}
                {effectiveLinkStatus(row) === "active" ? <Button size="sm" variant="danger" onClick={() => cancel(row)}>{tx("ยกเลิก", "Cancel")}</Button> : null}
              </div>
            ),
          },
        ]}
      />
    </>
  );
}
